import { EditorState, NodeSelection } from 'prosemirror-state';
import { CellSelection } from 'prosemirror-tables';
import { EditorView } from 'prosemirror-view';

/**
 * A command to select the node immediately after the given position.
 *
 * For example, `<p>foo</p>|<p>bar</p>` would select the second paragraph.
 * @param state The current state of the editor.
 * @param dispatch If provided the the command will be dispatched.
 * @param view The editor view.
 * @param pos The position to select the node after.
 * @param to The position to place the head table cell when the selection is a cell selection.
 * @returns Return true if the command can run.
 */
export type SelectNodeCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, pos?: number, to?: number) => boolean;

/**
 * Return a command to select the node immediately after the given position.
 *
 * For example, `<p>foo</p>|<p>bar</p>` would select the second paragraph.
 * @returns A command to select the node after the given position.
 */
export function selectNode(): SelectNodeCommand {
  return function (state: EditorState, dispatch: ProsemirrorDispatcher, view: EditorView, pos: number, to?: number): boolean {
    const $pos = state.doc.resolve(pos);
    const node = $pos.nodeAfter;

    if (!node || !NodeSelection.isSelectable(node)) {
      return false;
    }

    if (dispatch) {
      const tr = state.tr;
      if (node.type.name === 'table_cell' || node.type.name === 'table_header') {
        tr.setSelection(new CellSelection(state.doc.resolve(pos), state.doc.resolve(to || pos)));
      } else {
        tr.setSelection(NodeSelection.create(state.doc, pos));
      }
      dispatch(tr.scrollIntoView());
    }

    return true;
  };
}
